import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, X, ShieldCheck, MapPin, Calendar, Building2 } from 'lucide-react';

export interface DigitalBadgeAttendee {
  id?: string;
  fullName: string;
  email: string;
  phone?: string;
  companyName?: string | null;
  tier?: 'trade_visitor' | 'conference_delegate' | 'vip' | 'exhibitor' | 'exhibitor_pass' | string;
  primaryRole?: string;
  roles?: string[];
  ticketType?: string | null;
  qrToken?: string | null;
  badgeCode?: string | null;
  checkedInAt?: string | null;
  createdAt?: string | null;
  qrDataUrl?: string | null;
}

interface DigitalBadgeProps {
  attendee: DigitalBadgeAttendee;
  onClose?: () => void;
  onReset?: () => void;
}

export const DigitalBadge: React.FC<DigitalBadgeProps> = ({
  attendee,
  onClose,
  onReset,
}) => {
  const getBadgeTheme = (att: DigitalBadgeAttendee) => {
    const rawRole = (att.primaryRole || att.tier || '').toLowerCase().trim();

    // Check specific role or legacy tier
    switch (rawRole) {
      case 'vip':
        return {
          label: 'VIP Guest of Honour',
          badgeClass: 'border-[#E6C687] bg-[#FAF5EA] text-[#785613]',
          gradientBorder: 'from-[#C5A059] via-[#E6C687] to-[#C5A059]',
          accentText: 'text-[#785613]',
          lanyardColor: 'bg-gradient-to-r from-[#C5A059] via-[#E6C687] to-[#785613]',
        };
      case 'speaker':
        return {
          label: 'Keynote & Panel Speaker',
          badgeClass: 'border-rose-200 bg-rose-50 text-rose-800',
          gradientBorder: 'from-rose-600 via-red-600 to-rose-800',
          accentText: 'text-rose-800',
          lanyardColor: 'bg-gradient-to-r from-rose-700 via-red-700 to-rose-800',
        };
      case 'organiser':
      case 'organizer':
        return {
          label: 'Organising Committee',
          badgeClass: 'border-slate-800 bg-slate-900 text-white',
          gradientBorder: 'from-slate-900 via-slate-800 to-black',
          accentText: 'text-slate-900',
          lanyardColor: 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-950',
        };
      case 'media':
        return {
          label: 'Accredited Media & Press',
          badgeClass: 'border-orange-200 bg-orange-50 text-orange-800',
          gradientBorder: 'from-orange-500 via-amber-600 to-orange-700',
          accentText: 'text-orange-800',
          lanyardColor: 'bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700',
        };
      case 'sponsor':
        return {
          label: 'Official Sponsor',
          badgeClass: 'border-indigo-200 bg-indigo-50 text-indigo-800',
          gradientBorder: 'from-indigo-600 via-blue-600 to-indigo-800',
          accentText: 'text-indigo-800',
          lanyardColor: 'bg-gradient-to-r from-indigo-700 via-indigo-600 to-indigo-800',
        };
      case 'exhibitor':
      case 'exhibitor_pass':
        return {
          label: 'Official Exhibitor',
          badgeClass: 'border-sky-200 bg-sky-50 text-sky-800',
          gradientBorder: 'from-sky-600 via-cyan-600 to-sky-800',
          accentText: 'text-sky-800',
          lanyardColor: 'bg-gradient-to-r from-sky-700 via-cyan-600 to-sky-700',
        };
      case 'student_innovator':
        return {
          label: 'Student Innovator',
          badgeClass: 'border-lime-200 bg-lime-50 text-lime-800',
          gradientBorder: 'from-lime-600 via-green-600 to-lime-800',
          accentText: 'text-lime-800',
          lanyardColor: 'bg-gradient-to-r from-lime-700 via-green-700 to-lime-800',
        };
      case 'delegate':
        return {
          label: 'Distinguished Delegate',
          badgeClass: 'border-purple-200 bg-purple-50 text-purple-800',
          gradientBorder: 'from-purple-600 via-fuchsia-600 to-purple-800',
          accentText: 'text-purple-800',
          lanyardColor: 'bg-gradient-to-r from-purple-700 via-purple-600 to-fuchsia-700',
        };
      case 'contractor':
        return {
          label: 'Contractor & Site Crew',
          badgeClass: 'border-amber-200 bg-amber-50 text-amber-800',
          gradientBorder: 'from-amber-500 via-yellow-500 to-amber-600',
          accentText: 'text-amber-800',
          lanyardColor: 'bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-700',
        };
      case 'conference_delegate':
        return {
          label: 'Conference Delegate',
          badgeClass: 'border-emerald-200 bg-emerald-50 text-[#0A3D27]',
          gradientBorder: 'from-[#0A3D27] via-emerald-600 to-[#155e3b]',
          accentText: 'text-[#0A3D27]',
          lanyardColor: 'bg-gradient-to-r from-[#0A3D27] via-emerald-700 to-[#062B1B]',
        };
      case 'visitor':
      case 'trade_visitor':
      default: {
        if (att.ticketType === 'executive') {
          return {
            label: 'Executive Pass',
            badgeClass: 'border-[#E6C687] bg-[#FAF5EA] text-[#785613]',
            gradientBorder: 'from-[#C5A059] via-[#E6C687] to-[#C5A059]',
            accentText: 'text-[#785613]',
            lanyardColor: 'bg-gradient-to-r from-[#C5A059] via-[#E6C687] to-[#785613]',
          };
        }
        if (att.ticketType === 'professional') {
          return {
            label: 'Professional Pass',
            badgeClass: 'border-emerald-200 bg-emerald-50 text-[#0A3D27]',
            gradientBorder: 'from-[#0A3D27] via-emerald-600 to-[#155e3b]',
            accentText: 'text-[#0A3D27]',
            lanyardColor: 'bg-gradient-to-r from-[#0A3D27] via-emerald-700 to-[#062B1B]',
          };
        }
        return {
          label: 'Trade Visitor',
          badgeClass: 'border-teal-200 bg-teal-50 text-teal-800',
          gradientBorder: 'from-teal-600 via-emerald-600 to-teal-800',
          accentText: 'text-teal-800',
          lanyardColor: 'bg-gradient-to-r from-teal-700 via-emerald-700 to-teal-800',
        };
      }
    }
  };

  const theme = getBadgeTheme(attendee);
  const token = attendee.badgeCode || attendee.qrToken || 'IIAB27-PENDING';

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto print:max-w-none print:w-full print:m-0">
      {/* Top Action Bar (Hidden in Print) */}
      <div className="w-full flex items-center justify-between mb-4 print:hidden">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-[#0A3D27] animate-pulse" />
          <span className="text-xs font-semibold text-slate-700">Verified Digital Pass</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold transition-colors cursor-pointer shadow-2xs"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print / Save PDF</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 bg-white border border-slate-200 rounded-xl transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Lanyard Hole Mockup */}
      <div className="w-16 h-3 bg-slate-200 border border-slate-300 rounded-full mx-auto -mb-1.5 z-20 print:hidden" />

      {/* Physical Pass Card */}
      <div
        id="printable-badge"
        className={`relative w-full rounded-3xl p-1 bg-gradient-to-b ${theme.gradientBorder} shadow-xl overflow-hidden print:shadow-none print:p-0 print:border-2 print:border-black`}
      >
        <div className="bg-white rounded-[22px] p-6 text-slate-900 flex flex-col items-center relative overflow-hidden border border-slate-100 print:bg-white print:text-black print:p-8">
          {/* Badge Top Header */}
          <div className="w-full flex items-center justify-between pb-4 border-b border-slate-100 print:border-slate-300">
            <div className="flex items-center gap-2.5">
              <img
                src="/logo/iiab_logo.png"
                alt="IIAB Expo 2027 Logo"
                className="h-8 sm:h-9 w-auto object-contain print:h-8"
              />
              <div>
                <p className="text-xs font-black tracking-wider text-slate-900 print:text-black uppercase">
                  IIAB EXPO 2027
                </p>
                <p className="text-[10px] text-slate-500 print:text-slate-600">
                  JWCC Pavilion 3, Mumbai
                </p>
              </div>
            </div>

            {/* Access Tier Tag */}
            <span
              className={`text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full border ${theme.badgeClass} print:border-black print:text-black print:bg-slate-100`}
            >
              {theme.label}
            </span>
          </div>

          {/* Attendee Name & Company */}
          <div className="my-5 text-center w-full px-2">
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 print:text-black tracking-tight leading-tight">
              {attendee.fullName}
            </h2>
            <p className="text-sm font-semibold text-[#0A3D27] print:text-slate-700 mt-1 flex items-center justify-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              <span>{attendee.companyName || 'Trade Visitor / Independent'}</span>
            </p>
          </div>

          {/* Scannable QR Code Canvas */}
          <div className="p-3 bg-white rounded-2xl shadow-xs border border-slate-200 print:border-black flex flex-col items-center">
            <QRCodeSVG
              value={token}
              size={168}
              level="H"
              marginSize={1}
              bgColor="#ffffff"
              fgColor="#0A3D27"
            />
          </div>

          {/* Token String */}
          <div className="mt-3 text-center">
            <p className="font-mono text-xs font-bold text-slate-800 print:text-black tracking-widest uppercase">
              {token}
            </p>
            <p className="text-[9px] text-slate-500 print:text-slate-600 mt-0.5">
              Scan at JWCC Pavilion 3 Turnstiles
            </p>
          </div>

          {/* Event & Pass Metadata */}
          <div className="w-full mt-5 pt-4 border-t border-slate-100 print:border-slate-300 grid grid-cols-2 gap-3 text-[10px]">
            <div className="space-y-0.5">
              <span className="text-slate-500 print:text-slate-600 block">Dates</span>
              <span className="font-semibold text-slate-800 print:text-black flex items-center gap-1">
                <Calendar className="w-3 h-3 text-[#0A3D27]" />
                27 - 28 July 2027
              </span>
            </div>
            <div className="space-y-0.5 text-right">
              <span className="text-slate-500 print:text-slate-600 block">Location</span>
              <span className="font-semibold text-slate-800 print:text-black flex items-center justify-end gap-1">
                <MapPin className="w-3 h-3 text-[#0A3D27]" />
                Pavilion 3 • Exhibition Floor
              </span>
            </div>
          </div>

          {/* Security Watermark */}
          <div className="w-full mt-3 pt-2 border-t border-slate-100 print:border-none flex items-center justify-between text-[9px] text-slate-500">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-[#0A3D27]" />
              Official Holographic Gate Pass
            </span>
            <span className="font-mono font-bold text-slate-600">IIAB-AUTH</span>
          </div>
        </div>
      </div>

      {/* Footer Instructions (Hidden in Print) */}
      <div className="w-full mt-4 text-center print:hidden space-y-2">
        <p className="text-xs text-slate-500">
          Present this digital QR badge on your smartphone or carry a printed copy for instant badge ribbon printing at the venue entry.
        </p>
        {onReset && (
          <button
            onClick={onReset}
            className="text-xs text-[#0A3D27] hover:underline font-medium cursor-pointer"
          >
            Register another visitor or delegate
          </button>
        )}
      </div>
    </div>
  );
};

export default DigitalBadge;
